// Record Money In / Out — spec v2 §3.3.
//
// Single form replacing RecordReceipt + RecordPayment. Direction toggle
// at the top swaps which way the money went between the party and the rail:
//   in  → POST /api/transfers  party → rail  (the party paid the desk)
//   out → POST /api/transfers  rail → party  (the desk paid the party; optional fee on top, our cost)
// The treasury-only /api/receipts and /api/payments branches — money with no
// party on the other side — were removed on 5 Sept 2026: the External path
// that reached them was retired on 2 Sept and nothing has set partyKind since.
// The dead "treasury" branch of the party picker itself (a Combobox over
// treasury slugs, unreachable since 2 Sept) went with the sweep of 14 Sept
// 2026 (SWEEP-SCOPE-2026-09-13 §5.5).
//
// Used for the ~5% lifecycle case (customer wired AED but USDT not sent yet,
// or vice versa), and for standalone deposits/withdrawals not tied to a trade.
//
// KIND OF MOVEMENT (2026-08-13, chart of accounts): the operator picks what a
// movement *is* in one place — Party · Expense · Capital in · Drawing —
// instead of every kind of money leaving the desk looking like a customer
// payment. Only the first is this file's original flow, and it is untouched:
// the other three render from screens/RecordExpense.tsx, which owns their
// fields, their copy and their endpoints. No new button on the action bar —
// Record is where money movements already live (operator rule 2026-08-12).
//
// THE SWEEP OF 14 SEPT 2026 (§5.5, §7): words moved to "About this screen",
// the Reference box went (2 of 72 uses, both names — it fed the notes anyway),
// the cost split and Notes folded under More, every label from the §6
// glossary, the party's box in the standard before → after words (BoxLines),
// the success line naming amount, both sides and the reference. Every guard,
// the repeat panel, the held reference and the posting body are as they were.

import { useState, useMemo, useEffect, useRef } from 'react';
import { DeskSwitch, type Desk } from '../components/DeskSwitch';
import { errorWords, isSentinel, nothingLanded, ApiError, readWords } from '../lib/errors';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api, EXPENSE_CATEGORIES } from '../lib/api';
import { ScreenHeader } from '../components/ScreenHeader';
import { tg } from '../lib/telegram';
import {
  ASSETS, type AssetCode, treasuryPath, partyPath, makeReference,
  isFiat, DEFAULT_RAILS,
} from '../lib/workflow';
import { Combobox, type ComboboxOption } from '../components/Combobox';
import { rosterCut, usePartySearch } from '../lib/roster-cut';
import { RailPicker, type RailValue } from '../components/RailPicker';
import { RepeatPanel, useRepeatGuard, type RepeatKind } from '../components/RepeatPanel';
import { AsOfChips } from '../components/AsOfChips';
import { Field, FieldRow } from '../components/Field';
import { BoxLines, boxWords } from '../components/BoxLines';
import { ErrorLine } from '../components/ErrorLine';
import { AccountsDown, StateLine } from '../components/kit';
import { CopyRef } from '../components/CopyRef';
import { amountOk, amountProblem, optionalAmountProblem } from '../lib/amount';
import { partyName, partySubtitle, isStale, needsNameReview, rankForQuery, railName, sameAsset, foldAsset, partyBoxWords, dustFor, partyBalanceOf } from '../lib/naming';
import { displayAsset, fmtAmount, fmtDay, dubaiWallToIso, backdateProblem, isoToDubaiWall } from '../lib/format';
import { readState } from '../lib/read-state';
import { MoreDetails } from '../components/MoreDetails';
import { RecentEntries } from '../components/RecentEntries';
import { RecordExpense, RECORD_KIND_COPY, type RecordKind } from './RecordExpense';
import { RecordBetweenParties } from './RecordBetweenParties';
import { RecordProfitShare } from './RecordProfitShare';
import { RecordCharge } from './RecordCharge';

type Direction = 'in' | 'out';

/** What the movement IS. 'customer' is the original receipt/payment flow;
 *  'p2p' (11 Sept 2026) is a balance moved from one party to another, no desk
 *  account touched — its form lives in screens/RecordBetweenParties.tsx;
 *  'profit_share' (17 Sept 2026) is the desk's share of a sub-broker's
 *  balance, no cash moving — screens/RecordProfitShare.tsx; 'charge' (2 Oct
 *  2026) is a delivery charge raised on a party, in cash or in coin, on its
 *  own profit line — screens/RecordCharge.tsx. */
export type RecordMode = 'customer' | RecordKind | 'p2p' | 'profit_share' | 'charge';

// The chip says "Party" (§6 party picker: the label is Party everywhere but
// the cheque screen); the mode value stays 'customer' — a route, a palette
// entry and the ?mode= contract read it.
const MODES: ReadonlyArray<{ v: RecordMode; l: string }> = [
  { v: 'customer',     l: 'Party'           },
  { v: 'expense',      l: 'Expense'         },
  { v: 'capital',      l: 'Capital in'      },
  { v: 'drawing',      l: 'Drawing'         },
  { v: 'profit_share', l: 'Profit share'    },
  { v: 'charge',       l: 'Delivery charge' },
  { v: 'p2p',          l: 'Between parties' },
];
const isRecordMode = (v: unknown): v is RecordMode => MODES.some((m) => m.v === v);

/** A Book-trade link carrying this movement's facts (sweep items 29 and 39,
 *  14 Sept 2026): the party; the direction the movement implies; the amount
 *  seated on the side the party paid or received; the wallet when the asset
 *  is a coin; the instant; and settle-later ticked (?later=1 — NewTrade reads
 *  it), so the trade is settled BY the money already in the books instead of
 *  moving it a second time.
 *
 *  Direction, customer-side: the party RECEIVED a coin → customer buys; PAID
 *  a coin → customer sells; a currency the other way round (a currency in is
 *  the money side of a customer buy). Amount: what the party paid is the
 *  take side, what they received the give side.
 *
 *  THE RAIL RIDES ALONG WITH ITS SIDE (14 Sept 2026, evening). NewTrade used
 *  to seat ?rail on the coin side of the trade by the direction alone, so the
 *  link could only carry a wallet — a currency rail would have landed on the
 *  wrong side, and a euro receipt therefore opened Book trade in the default
 *  AED cash box. Now every link says which side the rail belongs on
 *  (?railSide=take when the party paid the desk, give when the desk paid the
 *  party — the side the amount is seated on), and NewTrade seats it there for
 *  a currency exactly as for a coin. */
function tradeHref(x: { party: string; direction: Direction; asset: string; amount: string; when: string; rail: string; later: boolean }): string {
  const q = new URLSearchParams();
  q.set('party', x.party);
  const coin = !isFiat(x.asset);
  q.set('dir', (x.direction === 'out') === coin ? 'customer_buy' : 'customer_sell');
  const side = x.direction === 'in' ? 'take' : 'give';
  q.set(side, x.amount);
  if (x.rail) { q.set('rail', x.rail); q.set('railAsset', x.asset); q.set('railSide', side); }
  if (x.when) q.set('when', x.when);
  if (x.later) q.set('later', '1');
  return `/trade?${q.toString()}`;
}

/** What the last posting said, kept apart from the live fields (review, 5 Sept 2026). */
export interface Posted {
  at: number; asOf: string | null; reference: string; deduped: boolean;
  /** The entry's own facts, for the money-without-a-trade line below
   *  (sweep item 29, 14 Sept 2026) — read from here, never from the live
   *  fields, which the operator may have moved on from by the time the box
   *  has been re-read. */
  slug: string; merchant: boolean; asset: string; direction: Direction; amount: string; rail: string;
  /** The two names the success line reads back (§6 success line, 14 Sept
   *  2026): the party and the account, as they were when this posted. */
  partyLabel: string; railLabel: string;
}

/** The answer to a posting: a headline of what happened (design system §3: one line, at most ten words — the amount
 *  and its unit one unbreakable value) and, under it in muted type, the two sides it moved between. A deduped
 *  answer says so instead: the ledger already held this reference and kept the first date. */
export function PostedLine({ posted }: { posted: Posted }) {
  if (posted.deduped) {
    return (
      <StateLine tone="warn" action={<Link to="/movements" className="btn-link text-meta">Money movements →</Link>}>
        Already recorded as <span className="font-mono">{posted.reference}</span> — nothing changed; check its date.
      </StateLine>
    );
  }
  return (
    <div className="space-y-0.5">
      <StateLine tone="good" action={<CopyRef value={posted.reference} />}>
        ✓ Recorded <span className="whitespace-nowrap">{fmtAmount(posted.amount, posted.asset)} {displayAsset(posted.asset)}</span> {posted.direction}
        {posted.asOf ? ` as of ${fmtDay(posted.asOf)}` : ''}
      </StateLine>
      <p className="text-meta text-tg-hint">
        {posted.direction === 'in' ? `${posted.partyLabel} → ${posted.railLabel}` : `${posted.railLabel} → ${posted.partyLabel}`}
      </p>
    </div>
  );
}

/** "About this screen" per mode — every sentence that used to sit in the
 *  form (§7 rule 6). Short paragraphs, the operator's words. The expense,
 *  capital and drawing texts are RecordExpense's (it owns that copy). */
function About({ mode }: { mode: RecordMode }) {
  if (mode === 'p2p') {
    return (
      <>
        <p>
          One party&rsquo;s balance moves to another&rsquo;s. No desk account moves and the desk earns
          nothing. &ldquo;And in return&rdquo; adds a leg going back, which makes it a swap between the two
          at their own terms.
        </p>
        <p>Any two of the two amounts and the rate fill the third.</p>
        <p>
          The two parties can be on different desks. The swap is still one entry: each leg carries its
          own pair between the desks, so both desks&rsquo; books tie.
        </p>
      </>
    );
  }
  if (mode === 'profit_share') {
    return (
      <>
        <p>
          The desk&rsquo;s share of a sub-broker&rsquo;s balance: the party&rsquo;s balance falls by the
          amount and the sub-broker cost line falls by the same, so profit rises. No cash moves and no
          desk account is touched — the cash half is recorded as Money out on its own.
        </p>
      </>
    );
  }
  if (mode === 'charge') {
    return (
      <>
        <p>
          A charge for bringing the money or the cheque to the customer. It is its own entry and goes onto
          its own profit line, Delivery charges — the trade or the cheque it belongs to is left as it is.
        </p>
        <p>
          Paid now puts the money into the account you pick. On their account means they owe it and nothing
          moves yet.
        </p>
        <p>
          Taken in a coin, it is counted in AED at the rate you type, and the coins are bought into the
          desk&rsquo;s stock at that rate — three entries, recorded in one go.
        </p>
      </>
    );
  }
  if (mode !== 'customer') return RECORD_KIND_COPY[mode].about;
  return (
    <>
      <p>
        Money in or out against a party, without a trade — a deposit before the coins go, a payment
        after they came. Both sides post together: the party&rsquo;s box and the account the money
        went through.
      </p>
      <p>
        <strong className="font-medium text-tg-text">As of</strong> is when the money moved. Now is
        the default; pick Yesterday or a time when you are writing down money that moved earlier —
        the reference carries that day, and a back-dated trade settles from money recorded around the
        same time.
      </p>
      <p>
        An account that has never held this asset asks once before opening it. A fee on top of a
        payment — a network fee, a commission, a courier charge — goes under More: the party gets the
        amount, the account pays the amount plus the fee, and the fee lands in profit.
      </p>
    </>
  );
}

export function RecordMoney({ initialMode = 'customer' }: { initialMode?: RecordMode } = {}) {
  const nav = useNavigate();

  // Routes (/record/expense, …) and the ⌘K palette land on a mode directly;
  // the selector still switches freely from there.
  // Arriving from a party page: ?party=<slug>&dir=in|out prefills who and which
  // way, so the operator does not retype a party they were already looking at.
  // The links used to carry ?party= and this screen ignored it entirely.
  const [search] = useSearchParams();
  const fromParty = search.get('party') ?? '';
  const fromDir   = search.get('dir') === 'out' ? 'out' : 'in';

  // ?mode=p2p (the party page's "Move to another party", the palette) lands on
  // a mode the way the /record/<mode> routes do — and keeps landing if the URL
  // changes while this screen is already mounted, since /record is one route.
  const modeParam = search.get('mode');
  const [mode, setMode] = useState<RecordMode>(isRecordMode(modeParam) ? modeParam : initialMode);
  useEffect(() => { if (isRecordMode(modeParam)) setMode(modeParam); }, [modeParam]);
  const [direction, setDirection] = useState<Direction>(fromDir as Direction);
  // ?asset= / ?rail= from a Chain row — the movement's own asset and wallet,
  // not the AED-cash default (operator, 1 Sept: book straight from the chain).
  const [asset, setAsset]         = useState<AssetCode>((search.get('asset') as AssetCode) || 'AED');
  // EXTERNAL RETIRED (operator, 2 Sept: "I highly doubt that button is ever
  // needed... for expenses we have a separate mechanism, and for capital in we
  // have one"). Every no-party flow already has its own honest door — Expense,
  // Capital in, Drawing, Move — and what remained was 'money vanished,
  // unclassified', the exact entry the closed books exist to prevent. Party
  // mode therefore ALWAYS has a party on the other side.
  const [slug, setSlug]           = useState(fromParty);
  const [amount, setAmount]       = useState(search.get('amount') ?? '');
  const [notes, setNotes]         = useState(search.get('note') ?? '');
  // Party movements must move a RAIL too — see the submit block for why.
  //
  // A LINK THAT NAMES THE ASSET MUST NOT LAND ON OFFICE CASH. Settlements'
  // Receive / Pay buttons carry ?asset= and ?amount= from the party's box but
  // no rail, because only the operator knows which pocket the money moved
  // through. Defaulting to cash.office answered that question for them: a USDT
  // leg opened on the AED cash rail, the red "that rail holds AED" line was
  // already on screen before anything was touched, and the asset box showed AED
  // while the state — and the posting — still said USDT. Start with no rail
  // instead, so the picker asks for the account and the operator answers.
  const [counterRail, setCounterRail] = useState<RailValue>(() => {
    const rail = search.get('rail'), railAsset = search.get('railAsset');
    if (rail && railAsset) return { slug: rail, asset: railAsset };
    if (search.get('asset')) return { slug: '', asset: '' };
    return { ...DEFAULT_RAILS.cash };
  });
  // ?cost= & ?costCategory= — "Book it again" after reversing a payment that
  // carried a fee on top hands the fee back separately (S7, 21 Sept 2026).
  const [costAmount, setCostAmount]   = useState(() => {
    const c = Number(search.get('cost')); return Number.isFinite(c) && c > 0 ? String(c) : '';
  });
  const [costCategory, setCostCategory] = useState(() => {
    const c = search.get('costCategory');
    return c && EXPENSE_CATEGORIES.some((x) => x.value === c) ? c : 'other';
  });

  // ── WHEN DID THIS HAPPEN? (5 Sept 2026) ───────────────────────────────────
  // The other half of the Loki 3 Sept case (see NewTrade): a back-dated trade
  // is settled by the sweep from money recorded around the same time, so money
  // written down late must be able to carry the time it really moved. Empty
  // means now. The value is Dubai wall-clock, read through lib/format.ts — a
  // datetime-local carries no zone, and the books have one.
  // ?when=2026-09-11T10:02 (Dubai wall-clock) rides in from a Chain row — the
  // block time is when the money actually moved.
  const [when, setWhen] = useState(search.get('when') ?? '');
  // The time a Chain row (or a Record money link) sent along: shown as its own chip.
  const urlWhen = search.get('when') ?? '';
  const whenIso   = when ? dubaiWallToIso(when) : '';
  const whenWords = backdateProblem(when);
  // WHAT THE LAST POSTING SAID, kept apart from the live field (review, 5 Sept
  // 2026 — the same fault as NewTrade). The success line read `whenIso` and
  // nothing cleared `when` on success, so the next movement typed straight
  // after a back-dated one went out on that earlier day too. The date now
  // leaves with the entry it was typed for (onSuccess), and this holds what
  // was actually POSTED: the instant, the reference, and whether the BFF
  // deduped it — a retry whose first attempt had landed keeps that attempt's
  // date, whatever this request said.
  const [posted, setPosted] = useState<Posted | null>(null);

  // Rail balances drive the picker for party movements — the operator has to
  // say which pocket the money actually moved through.
  // WHICH DESK'S ACCOUNT the money moved through. The party stays a DubaiOTC
  // party; the account may be an India bank (6 Sept 2026: "how do I pay INR
  // from RBL to Loki OTC" — the picker only ever listed Dubai's rails, so the
  // operator had typed "rbl" into the Customer box). A cross-desk posting is
  // one transaction, rail on one desk and party on the other; the books hold
  // both desks under one roof and the inter-desk bridge is a pending item
  // (registry §2, Inter-entity).
  const [railDesk, setRailDesk] = useState<Desk>('dubaiotc');
  const treasuryRows = useQuery({
    queryKey: ['treasury', 'balances', railDesk],
    queryFn:  () => api.treasuryBalances(railDesk),
    // The asset list below is derived from what the chosen rail actually holds.
    enabled:  mode === 'customer',
  });
  // What the account list is worth — `readState` (lib/read-state.ts; Appendix B): 'known' only once a read SUCCEEDED; a failed refresh keeps its
  // old rows beside the error, a retry of a failure and an offline pause have none. A guard needs 'known'; a banner says 'failed' / 'waiting'.
  const accounts = readState(treasuryRows);
  // No list at all because the read FAILED (a failed REFRESH keeps its rows: that picker stays and is told, readFailed): an open picker would say "No such account" and point at New party.
  const accountsDown = accounts === 'failed' && !treasuryRows.data;

  // ── WHAT DOES THE CHOSEN RAIL ACTUALLY HOLD? ────────────────────────────
  //
  // The asset used to be a free dropdown standing next to the rail, defaulted
  // to AED. On 2026-08-27 that put 4,500,000 AED onto the INR Angadiya rail and
  // minted `cash:angadiya:fiat:aed` — a rail that had never existed, holding
  // the wrong currency beside the real one.
  //
  // RailPicker has ALWAYS returned {slug, asset} together — the screen was
  // throwing the asset away and trusting its own dropdown. Now the rail decides,
  // and the dropdown only offers what that rail carries. Balance, not history,
  // for the same reason the BFF guard uses balance: a reverted phantom leaves
  // volumes behind forever and would re-offer the wrong asset.
  const railSlug = counterRail.slug;
  const railAssets = useMemo(() => {
    // What an account HOLDS is read off its balance. A failed REFRESH keeps the old rows beside the error, and a retry has none (design
    // system Appendix B): the holding is unknown then — the dropdown offers the full list and no "That account holds …" is said from them.
    if (accounts !== 'known') return [];
    const rows = treasuryRows.data?.rows ?? [];
    const held = rows
      .filter((r) => r.slug === railSlug && Number(r.balance) !== 0)
      .map((r) => r.asset as AssetCode);
    return [...new Set(held)];
  }, [treasuryRows.data, accounts, railSlug]);
  // No rail chosen, or a brand-new one: fall back to the full list rather than
  // an empty dropdown the operator cannot get out of.
  //
  // THE BOX ALWAYS OFFERS WHAT IT IS HOLDING. A <select> whose value is not
  // among its options renders the FIRST option and keeps the other value in
  // state — the box says AED and the posting says USDT. The folded code a
  // party's box speaks ('USDT') is not in ASSETS at all, so a Settlements link
  // arrives holding exactly such a value. Keep it listed until a rail replaces
  // it, and the two can never disagree.
  const assetChoices: AssetCode[] = useMemo(() => {
    const base = railAssets.length ? railAssets : [...ASSETS];
    return base.some((a) => a === asset) ? base : [asset, ...base];   // dialect-ok: this asks what the option list literally holds, not whether two codes mean the same coin
  }, [railAssets, asset]);
  // sameAsset, never includes(): the rail speaks the v3 fold (USDT) while the
  // dropdown may speak the chain form (USDT·TRC) — the guard fired on the SAME
  // dollar and refused honest postings (operator hit it 2 Sept).
  const assetMismatch = railAssets.length > 0 && !railAssets.some((a) => sameAsset(a, asset));
  // SNAP THE INCOMING CODE ONTO THE OPTION THAT IS SHOWN (operator, 3 Sept: a
  // Chain chip for a 56,426 USDT·ERC receipt opened this screen reading ETH).
  // The chip speaks the chain code (USDTERC), the rail's list speaks the fold
  // (USDT); a <select> holding a value it cannot display shows its first
  // option instead. Same dollar, one spelling — the list's.
  useEffect(() => {
    if (railAssets.length === 0) return;
    const exact = railAssets.some((a) => a === asset);   // dialect-ok: exact option match; the fold is checked right below
    if (exact) return;
    const same = railAssets.find((a) => sameAsset(a, asset));
    if (same) setAsset(same);
  }, [railAssets, asset]);

  // ── party options ───────────────────────────────────────────────────────
  const parties = usePartySearch({
    base: ['parties', 'dubaiotc'], text: slug,
    ask: (q) => api.listParties({ entity: 'dubaiotc', q, with_balances: true }),
    enabled: mode === 'customer' && slug.length > 0,
  });
  // The selected party's TYPE decides which account its leg posts to. Without
  // it every merchant desk gets a second, parallel account — see partyPath().
  const selectedParty = useMemo(
    () => (parties.data?.parties ?? []).find((p) => p.slug === slug),
    [parties.data, slug],
  );
  const selectedPartyType = selectedParty?.party_type;
  // WHAT THE LIST IS WORTH — `readState` (lib/read-state.ts; Appendix B): the names, and the balances (a list the BFF flags
  // `balances_read: false` has the names and not the balances: its empty balances are not "square"). The picker's box words take the
  // balances' answer.
  const lookup = readState(parties);
  const listBalances = readState(parties, { balances: true });
  const listBalancesRead = listBalances === 'known';
  // That unread list says so once, with a Retry, whatever is typed (one query per search; the flag is read off the list showing).
  const listBalancesFlagged = lookup === 'known' && listBalances === 'failed';

  const partyOptions: ComboboxOption[] = useMemo(() =>
    // OWN ACCOUNTS ARE NOT CUSTOMERS. Every rail carries a party record so the
    // Parties screen can show it — `rail-pr-hk` IS `cash.angadiya`. Offered
    // here, picking one posts to `customer:dubaiotc:rail-pr-hk`: a phantom party
    // holding money beside the real rail, and the rail itself never moves.
    // NewTrade has always filtered these; this screen never did (found by the
    // operator 2026-08-27, when the Customer box auto-completed to `rail-pr-hk`).
    rankForQuery((parties.data?.parties ?? []).filter((p) => p.party_type !== 'own_account'), slug).map((p) => ({
      value:     p.slug,
      label:     partyName(p),
      secondary: [partySubtitle(p, { compact: true }), listBalancesRead ? partyBoxWords(p) : ''].filter(Boolean).join(' · '),
      stale:     isStale(p),
      review:    needsNameReview(p.display_name),
    }))
  , [parties.data, listBalancesRead, slug]);

  // ── BOTH SIDES, BEFORE AND AFTER ────────────────────────────────────────
  // An entry moves two accounts and the screen showed only one of them: the
  // rail's balance sat in the picker, the party's was nowhere. So "does this
  // leave HK square?" meant leaving the screen to find out.
  const partyExists = !!selectedParty;
  const partyBalances = useQuery({
    queryKey: ['party', slug, 'balances'],
    queryFn:  () => api.partyDetail(slug, 'dubaiotc'),
    enabled:  partyExists,
    staleTime: 30_000,
  });
  // The party's box is drawn — and anything is said from it — only once a read SUCCEEDED (see lib/read-state.ts).
  const box = readState(partyBalances);
  const amountN   = Number(amount) || 0;
  const costN     = Number(costAmount) || 0;
  // WHAT WAS TYPED, CHECKED BEFORE IT IS SENT. '1,000' is NaN, reaches the API
  // as null and comes back as a 500 about decimal strings; a minus sign posts
  // and runs the entry backwards. One rule for every amount box (lib/amount.ts).
  const amountWords = amountProblem(amount);
  const costWords   = optionalAmountProblem(costAmount);
  // Sign law: money IN means the party paid the desk — their tracker falls.
  // fold-probe: party balances arrive FOLDED from the BFF (stablecoins
  // netted under 'USDT'), the screen's asset may be chain-suffixed.
  const partyNow  = partyBalanceOf(partyBalances.data?.balances, asset);
  // THE FEE IS ON TOP (operator, 17 Sept 2026: "fees box seems wrong, amount
  // is getting deducted instead of adding"). The Amount box is what the PARTY
  // gets; the cost is what the account pays over and above it — the same
  // reading as Move money's "sender pays" and the venue withdrawals, where
  // the fee is charged on top of the coins sent. party_payment_split.num
  // still sends the cost to the expense account and the rest to the party,
  // so the account is debited amount + cost. Before this the cost was taken
  // OUT of the amount and Abdellah's 6.289 payout credited him 6.28898076.
  const toPartyN  = amountN;
  const railDebitN = direction === 'out' ? Number((amountN + costN).toFixed(8)) : amountN;
  const partyNext = partyNow + (direction === 'in' ? -amountN : toPartyN);

  // ── MONEY WITHOUT A TRADE (sweep 3.1 #6 / item 29, 14 Sept 2026) ──────────
  // Coins moved to or from a MERCHANT and no trade explained them: N40K3 was
  // booked two days after its money, 6KB2I eight days after, and HK's
  // 1,00,000 USDT of 8 Sept still had no trade on the 13th. This screen never
  // asked what a movement was for. Now, after a posting to or from a
  // merchant, it reads the box back and — when the box carries a coin
  // position with no awaiting trade in that asset — says so in one line,
  // with Book trade prefilled. Read from what was POSTED and from the two
  // re-reads the global refresh makes after every posting (the party's box,
  // the settle-later list — the one read that says which assets still have a
  // trade open), never from the live fields. Nothing automatic: a sentence
  // and a link.
  // READ ONCE A PARTY IS KNOWN (sweep item 23, 15 Sept 2026): the same list
  // feeds the dating line below, which has to speak BEFORE the posting —
  // so the read is no longer held back until a merchant coin movement has
  // posted. One key, one cache entry: the after-posting re-read is the
  // global refresh's, as before.
  // Is the open-trades check WANTED (the same conditions as ever: a party picked, or a coin movement just posted to a merchant)? When it is
  // and its read failed, the line below says it was not done — a check that could not run found nothing to say, but did not say so.
  const settlementsWanted = partyExists || (!!posted && posted.merchant && !isFiat(posted.asset));
  const settlements = useQuery({
    queryKey: ['settlements'],
    queryFn:  () => api.settlements(),
    enabled:  settlementsWanted,
    staleTime: 15_000,
  });
  const settlementsRead = readState(settlements);
  const nudge = useMemo(() => {
    if (!posted || posted.deduped || !posted.merchant || isFiat(posted.asset)) return null;
    if (posted.slug !== slug) return null;   // the box on screen is somebody else's now
    // Both reads must be NEWER than the posting: the box and the open list
    // with this movement in them, not the ones the entry was typed against.
    if (!partyBalances.data || partyBalances.dataUpdatedAt <= posted.at) return null;
    if (!settlements.data || settlements.dataUpdatedAt <= posted.at) return null;
    // A failed refresh keeps the old rows beside the error, and a retry has none: "no trade behind it" is said only from two reads that SUCCEEDED.
    if (box !== 'known' || settlementsRead !== 'known') return null;
    const held = partyBalanceOf(partyBalances.data.balances, posted.asset);
    if (Math.abs(held) <= dustFor(posted.asset)) return null;
    const open = settlements.data.parties.find((p) => p.slug === posted.slug);
    if (open?.open_assets.some((a) => sameAsset(a, posted.asset))) return null;
    return { held, name: partyName(partyBalances.data.party) };
  }, [posted, slug, partyBalances.data, partyBalances.dataUpdatedAt, box, settlements.data, settlements.dataUpdatedAt, settlementsRead]);

  // ── THE DATING LINE (sweep §4.3 ix / item 23, 15 Sept 2026) ──────────────
  // Money written down with As of left on Now, when it actually moved
  // yesterday, carries the wrong day: the reference and the statement say
  // today, and the sweep's pool — which opens a day before the oldest
  // awaiting trade and runs forward — still finds it, but the desk's own
  // record of WHEN it moved is false (Loki, 3 Sept: the trade was typed two
  // days late and the dates never lined up again). So, while the chips say
  // Now and the party picked has a settle-later trade booked more than 12 h
  // ago with an open leg in the asset being recorded, this side of it (money
  // in is the take leg — what they pay; money out the give leg — what they
  // get), one amber line names the trade's day and offers to set As of to
  // its instant. A nudge, not a gate: Record stays enabled, nothing is set
  // without the tap. The oldest such trade is named — money recorded at its
  // instant is inside every later trade's window too. A trade dated before
  // the books opened is left alone: As of could not be set to it anyway
  // (backdateProblem), and the sweep never draws for one.
  const dating = useMemo(() => {
    if (!selectedParty || when !== '' || settlementsRead !== 'known' || !settlements.data) return null;   // a failed refresh keeps the old list, a retry has none: no nudge from either
    const open = settlements.data.parties.find((p) => p.slug === slug);
    if (!open) return null;
    const side = direction === 'in' ? 'take' : 'give';
    const cutoff = Date.now() - 12 * 3_600_000;
    let pick: { at: string; asset: string } | null = null;
    for (const d of open.deals) {
      if (d.lifecycle !== 'awaiting' || !d.legs) continue;
      const leg = d.legs[side];
      if (leg.settled || !(leg.open > 0) || !sameAsset(leg.asset, asset)) continue;
      const t = Date.parse(d.booked_at);
      if (!Number.isFinite(t) || t > cutoff) continue;
      if (backdateProblem(isoToDubaiWall(d.booked_at))) continue;
      if (!pick || t < Date.parse(pick.at)) pick = { at: d.booked_at, asset: leg.asset };
    }
    return pick && { ...pick, name: partyName(selectedParty) };
  }, [selectedParty, slug, when, direction, asset, settlements.data, settlementsRead]);

  // ── submit ─────────────────────────────────────────────────────────────
  // The repeat guard's panel is shared with Move money and Inter-entity —
  // every screen that posts a transfer meets the same 409.
  const { twin: repeat, clearTwin, guard } = useRepeatGuard();

  // THE REFERENCE IS THE IDEMPOTENCY KEY, SO IT MUST SURVIVE A RETRY (review,
  // 5 Sept 2026 — the lesson NewTrade's deal id learned, arriving here late).
  // It was minted inside post(), which runs on EVERY submit: 230,000 AED
  // recorded as of 3 Sept, a timeout after the BFF had posted, Record pressed
  // again — and the second attempt carried a fresh RCPT-2026-09-03-… that the
  // ledger could not recognise as the same money, so it posted twice. Held in
  // a ref, not state: it must not re-render, and it must not reset when the
  // operator edits a field mid-retry. Cleared on success, and by
  // retireReference() when the prefix or the day it carries would change.
  const refRef = useRef<string | null>(null);
  /** Which refusals the operator has already answered for THIS entry (the
   *  Swap form's rule, 14 Sept 2026): the twin guard (confirm_repeat), the
   *  settled-trade guard (confirm_money_moved_again — the transfers-side
   *  mirror of Book trade's cross-shape guard) and the new-rail door
   *  (allow_new_rail_asset). The BFF asks them in that order on every POST,
   *  so a re-POST has to carry every yes given so far or an earlier door
   *  refuses again. Cleared with the reference: a new entry answers afresh. */
  const confirmedRef = useRef<Set<RepeatKind | 'new_rail'>>(new Set());

  // The account's name as the picker shows it — for the success line and the
  // new-account panel. A row the list does not carry (a prefill from a link)
  // still reads through railName, so a raw slug never reaches the screen.
  const railLabel = railName(
    (treasuryRows.data?.rows ?? []).find((r) => r.slug === counterRail.slug && sameAsset(r.asset, counterRail.asset))
      ?? { slug: counterRail.slug },
  );

  const post = async () => {
      // THE PARTY MUST HAVE BEEN READ: its TYPE picks the account the leg posts to (partyPath), and a party that has not been read — a search
      // still on its way, a failed one, a name the books do not hold — would be posted as a customer: a merchant's money on a phantom
      // customer account (Screens' Astra review, 23 Oct 2026). Every door that books comes through here, the button's own gate included.
      if (!selectedParty || lookup !== 'known') throw new Error('The party has not been read — wait for the list, or pick one from it.');
      // A movement involving a PARTY has two sides and must post both: the
      // party's balance AND the rail the money physically moved through.
      // Before 2026-08-13 this screen posted only one — inflow/outflow send to
      // @world, so "customer deposits 10,000 cash" either credited the customer
      // with no cash arriving anywhere, or banked the cash without recording
      // that the desk now owes them. Every such entry was half a transaction.
      // LEDGER-STANDARD called this out; transfer/transfer_party already had
      // the right shape.
      //
      // Party mode ALWAYS has a party on the other side (External retired
      // 2 Sept), so this is the only posting left. The treasury-only receipt
      // and payment branches that used to follow were unreachable — nothing
      // set partyKind — and were removed on 5 Sept 2026.
      const party = partyPath(slug, selectedPartyType);
      const rail  = treasuryPath(counterRail.slug, asset, railDesk);   // the account's own desk
      // The reference carries the day the money MOVED (RCPT-2026-09-03-… for
      // 3 Sept money typed on the 5th), like the deal id does — minted once
      // per entry, see refRef.
      refRef.current ??= makeReference(direction === 'in' ? 'RCPT' : 'PAY', whenIso || undefined);
      const reference = refRef.current;
      const data = await api.transfer({
        // in  = the party pays the desk: their balance falls, the rail rises
        // out = the desk pays the party: the rail falls, their balance rises
        source:      direction === 'in' ? party : rail,
        destination: direction === 'in' ? rail  : party,
        // Out with a cost: the account pays amount + cost, the party gets the
        // amount (see railDebitN).
        amount:      { asset, amount: direction === 'out' && costN > 0 ? railDebitN : Number(amount) },
        fee_payer:   'house',
        reference,
        // The Reference box that used to ride in here went on 14 Sept 2026
        // (§5.5: 2 of 72 uses, both names) — Notes is the one free-text box.
        notes:       notes.trim() || undefined,
        confirm_repeat: confirmedRef.current.has('repeat') || undefined,
        // The operator said yes to opening this asset on this rail (see the
        // new-rail panel below).
        allow_new_rail_asset: confirmedRef.current.has('new_rail') || undefined,
        // Only when set — absent means now (5 Sept 2026, see `when`).
        effective_at: whenIso || undefined,
        // A commission or courier charge inside an outgoing payment is a cost
        // to the desk, not money the party keeps.
        cost: direction === 'out' && costN > 0
          ? { amount: { asset, amount: costN }, category: costCategory }
          : undefined,
        // "This money really moved a second time" — the transfers-side guard's
        // own yes (14 Sept 2026; TransferBody names it since the same evening).
        confirm_money_moved_again: confirmedRef.current.has('money') || undefined,
      });
      // WHAT WAS SENT RIDES BACK WITH THE ANSWER, so the success line reports
      // the instant and reference that were posted — never whatever the
      // fields hold by the time it renders (see `posted`) — and the entry's
      // facts ride with it for the money-without-a-trade line.
      return {
        data, reference, asOf: whenIso || null,
        slug, merchant: selectedPartyType === 'counterparty', asset, direction, amount, rail: counterRail.slug,
        partyLabel: selectedParty ? partyName(selectedParty) : slug, railLabel,
      };
  };
  const submit = useMutation({
    // REPEAT GUARD (BFF 409 {error:'repeat_suspected', twin}): the same money
    // is already in the books. Name the twin, let the operator decide. The
    // confirmations a re-POST carries live in confirmedRef, not in the call.
    mutationFn: () => guard(post),
    onSuccess: ({ data, reference, asOf, ...facts }) => {
      clearTwin();
      refRef.current = null;
      confirmedRef.current.clear();
      // A deduped answer means the ledger already held this reference — a
      // first attempt whose response was lost — and kept ITS date; the date
      // in this request was ignored, so it must not be reported as posted.
      setPosted({ at: Date.now(), asOf, reference: data.reference ?? reference, deduped: !!data.deduped, ...facts });
      // The figures leave with the entry they were typed for (§7 rule 12,
      // 14 Sept 2026): the date rode into the next movement before 5 Sept,
      // and an amount left in the box is a second tap away from posting
      // twice. The party, the direction and the account stay — the next
      // entry is usually the same party's.
      setWhen(''); setAmount(''); setCostAmount(''); setNotes('');
      tg()?.HapticFeedback.notificationOccurred('success');
      // Booked FROM a chain row (?note=chain …): fire the matcher NOW so the
      // pending row is gone before the operator looks back (operator, 2 Sept:
      // "make it auto vanish once trade booked and matched"). Fire-and-forget —
      // a failed sync just leaves the row for the screen's own auto-sync.
      if ((search.get('note') ?? '').startsWith('chain ')) {
        api.chainSync().catch(() => {});
      }
    },
    onError:   (e) => { if (!isSentinel(e)) tg()?.HapticFeedback.notificationOccurred('error'); },
  });

  /** Drop the held reference so the next post mints its own — the prefix says
   *  the direction and the stamp says the day, so either changing makes the
   *  old one a lie. UNLESS the books may already hold it: a posting in flight,
   *  or a last failure that was not provably answered before the ledger write
   *  (lib/errors.ts nothingLanded — a timeout or 5xx can come back with the
   *  transfer posted). There a fresh reference is how the money posts twice,
   *  and the kept one makes the retry a no-op the BFF answers `deduped`. */
  const retireReference = () => {
    const mayHold = submit.isPending || (submit.isError && !nothingLanded(submit.error));
    if (!mayHold) { refRef.current = null; confirmedRef.current.clear(); }
  };

  // The one toggle (styles.css, emphasis rule): the picked mode is lit.
  const segmented = (active: boolean) => `seg whitespace-nowrap px-3 ${active ? 'seg-on' : ''}`;
  // A READ THAT FAILED IS NOT AN EMPTY ONE (the party list, the rail list and the party's box each drew nothing, or a
  // "square", and said nothing). One line: what failed and the way out; the books' own sentence behind a tap.
  const retry = (again: () => unknown) => <button type="button" className="btn-link text-meta" onClick={() => void again()}>Retry</button>;
  // The chosen chip is on screen when the mode changes — a deep link to
  // Profit share landed with its chip cut off at the strip's right edge
  // (19 Sept 2026). Sideways only: the strip's own scroll, never main's.
  const modeChip = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const el = modeChip.current;
    const strip = el?.parentElement;
    if (!el || !strip) return;
    const left = el.offsetLeft - 8;
    const right = el.offsetLeft + el.offsetWidth + 8;
    if (left < strip.scrollLeft) strip.scrollLeft = left;
    else if (right > strip.scrollLeft + strip.clientWidth) strip.scrollLeft = right - strip.clientWidth;
  }, [mode]);

  return (
    <div className="space-y-3 lg:max-w-[720px] lg:mx-auto lg:space-y-4">
      <button onClick={() => nav(-1)} className="btn-link text-meta">← Back</button>
      <ScreenHeader
        title={mode === 'customer' ? 'Record money'
          : mode === 'p2p' ? 'Between parties'
          : mode === 'profit_share' ? 'Profit share'
          : mode === 'charge' ? 'Delivery charge'
          : RECORD_KIND_COPY[mode].title}
        // 21 Oct 2026 audit: Party mode has no subtitle — "Money in or out against
        // a party — moved without a trade." restated the heading and the Money in /
        // Money out toggle under it; its one fact ("without a trade") is the first
        // line of About now.
        subtitle={mode === 'customer'
          ? undefined
          : mode === 'p2p'
            ? 'One party’s balance to another’s — no desk account moves.'
            : mode === 'profit_share'
              ? 'The desk’s share of a sub-broker’s balance — no cash moves.'
              : mode === 'charge'
                ? 'In cash or in coin — its own profit line.'
                : RECORD_KIND_COPY[mode].subtitle}
        help={<About mode={mode} />}
      />

      {/* What kind of movement — the chart's five answers in one place. One
          scrolling chip row on the phone (§5.5 alignment: a 2×2 grid put
          five chips in three rows with an orphan); lg+ a content-sized
          segmented control, like every other picker here. */}
      <div className="flex gap-2 overflow-x-auto [scrollbar-width:none] -mx-1 px-1 lg:mx-0 lg:px-1 lg:inline-flex lg:w-auto lg:gap-1 lg:p-1 lg:rounded-lg lg:border lg:border-stroke lg:bg-panel">
        {MODES.map((m) => (
          <button
            key={m.v}
            ref={mode === m.v ? modeChip : undefined}
            onClick={() => { setMode(m.v); retireReference(); }}
            className={segmented(mode === m.v)}
          >
            {m.l}
          </button>
        ))}
      </div>

      {/* Keyed on the link's parties so a fresh deep link (?from=…&to=…) onto an
          already-mounted screen starts a fresh form. */}
      {mode === 'p2p' && <RecordBetweenParties key={`p2p-${search.get('from') ?? ''}-${search.get('to') ?? ''}`} />}
      {mode === 'profit_share' && <RecordProfitShare />}
      {/* Keyed on the link's facts so a fresh deep link (a trade's page, a
          cheque's page, a Chain row) onto an already-mounted screen starts a
          fresh form. */}
      {mode === 'charge' && <RecordCharge key={`chg-${search.get('party') ?? ''}-${search.get('deal') ?? ''}-${search.get('note') ?? ''}`} />}
      {mode !== 'customer' && mode !== 'p2p' && mode !== 'profit_share' && mode !== 'charge' && <RecordExpense key={mode} kind={mode} />}

      {/* THE LAST TEN OF THIS KIND under the other forms too (operator, 17
          Sept 2026: "10 last tx of that same tx type"): the desk's costs,
          the owner's capital or drawings, balances moved between parties —
          each from the movement ledger's own kind. Profit share has none:
          the ledger files it under Adjustment with every other correction,
          and a list that mixed them would not be "that same type". */}
      {mode === 'expense' && <RecentEntries kind="entries" movementKind="expense" title="Last 10 costs" />}
      {mode === 'capital' && <RecentEntries kind="entries" movementKind="owner" direction="in" title="Last 10 capital in" />}
      {mode === 'drawing' && <RecentEntries kind="entries" movementKind="owner" direction="out" title="Last 10 drawings" />}
      {mode === 'p2p' && <RecentEntries kind="entries" movementKind="p2p" title="Last 10 between parties" />}

      {mode === 'customer' && (
      <>
      {/* Direction — Money in stays the default (receipts are the majority);
          the before → after line under the party and the button both carry
          the direction, so a wrong one reads wrong (§5.5). */}
      <div className="flex gap-2 lg:inline-flex lg:w-auto lg:gap-1 lg:p-1 lg:rounded-lg lg:border lg:border-stroke lg:bg-panel">
        {([
          { v: 'in',  l: 'Money in'  },
          { v: 'out', l: 'Money out' },
        ] as const).map((d) => (
          <button
            key={d.v}
            // The prefix (RCPT / PAY) follows the direction — see refRef.
            onClick={() => { setDirection(d.v); retireReference(); }}
            className={`flex-1 ${segmented(direction === d.v)}`}
          >
            {d.l}
          </button>
        ))}
      </div>

      {/* Which desk's accounts the picker lists — its own row (§5.5: inside
          the account field it pushed the picker 36px below the party box).
          Renders only for an admin who sees both desks. */}
      <DeskSwitch value={railDesk} onChange={(d) => { setRailDesk(d); setCounterRail({ slug: '', asset: '' }); }} />

      {/* Two reads down is one banner (design system §5), naming both. */}
      {(lookup === 'failed' || accounts === 'failed') && (
        <StateLine
          tone="warn"
          more={readWords(lookup === 'failed' ? parties.error : treasuryRows.error)}
          action={retry(() => { if (lookup === 'failed') void parties.refetch(); if (accounts === 'failed') void treasuryRows.refetch(); })}
        >
          {`Couldn’t read ${lookup === 'failed' && accounts === 'failed' ? 'parties and accounts' : lookup === 'failed' ? 'parties' : 'the accounts'} — try again.`}
        </StateLine>
      )}
      {listBalancesFlagged && (
        <StateLine tone="warn" action={retry(parties.refetch)}>
          Balances not read — try again.
        </StateLine>
      )}

      {/* Row 1: Party | Amount · Row 2: Money lands in | As of (§5.5). */}
      <FieldRow>
        <Field label="Party">
          <Combobox
            value={slug}
            onChange={setSlug}
            options={partyOptions}
            more={rosterCut(parties.data)}
            placeholder="Search party"
            // A list still being read (or paused) is not "no party by that name" either.
            loading={slug.length > 0 && lookup === 'waiting'}
            emptyHint={slug.length === 0 ? null : lookup === 'failed' ? 'Parties could not be read.' : undefined}
          />
          {!partyExists && lookup === 'known' && slug.trim() !== '' && (
            <p className="text-meta text-tg-hint mt-1">Pick a party from the list.</p>
          )}
          {partyExists && (
            // A failed balance read has no box: never a "square → …" drawn from a zero that was never read — nor one that has not answered yet
            // (a first read, the retry of a failure, an offline pause).
            box === 'failed'
              ? <StateLine tone="warn" className="mt-1" more={readWords(partyBalances.error)} action={retry(partyBalances.refetch)}>Couldn&rsquo;t read the balance — try again.</StateLine>
            : box === 'waiting'
              ? <p className="text-meta text-tg-hint mt-1">Checking balance…</p>
              // The party's box speaks the fold (USDT) — 21 Oct 2026 audit: the line
              // named the account's chain ("USDT·TRC · owes you 250,000") for a box
              // that holds bare USDT. partyNow is read by the same fold.
              : <BoxLines className="mt-1" asset={foldAsset(asset)} before={partyNow} after={amountN > 0 ? partyNext : undefined} />
          )}
        </Field>

        <Field label="Amount">
          <div className="flex gap-2">
            <input className="input flex-1" value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" />
            {/* 21 Oct 2026 audit: at w-24 the box cut its own code ("USDT·TR") — an
                asset the operator is about to post in. Wide enough for the longest
                one ("USDC·BASE"): 16px text on a phone, 14px (the body role) at lg+ —
                the design-system sweep moved lg from 13 to 14px, which clipped it again
                at w-28, so lg is w-32. */}
            <select
              className={`select w-36 lg:w-32 shrink-0 ${assetMismatch ? 'border-neg' : ''}`}
              value={asset}
              onChange={(e) => setAsset(e.target.value as AssetCode)}
            >
              {assetChoices.map((a) => <option key={a} value={a}>{displayAsset(a)}</option>)}
            </select>
          </div>
          <ErrorLine>{amountWords}</ErrorLine>
          {assetMismatch && (
            <ErrorLine>
              That account holds {railAssets.map(displayAsset).join(' / ')}, not {displayAsset(asset)}.
            </ErrorLine>
          )}
        </Field>

        {/* A party movement has two sides. This is the second one: the pocket the
            money physically moved through. Required — without it the entry is
            half a transaction (the bug fixed 2026-08-13). */}
        <Field label={direction === 'in' ? 'Money lands in' : 'Paid from'}>
          {accountsDown ? <AccountsDown value={counterRail} /> : (
          <RailPicker
            value={counterRail}
            onChange={(v) => {
              setCounterRail(v);
              // The picker hands back the rail's asset. Take it.
              if (v.asset) setAsset(v.asset as AssetCode);
            }}
            rows={treasuryRows.data?.rows ?? []}
            // The party's own habits rank the list (12 Sept 2026), once the
            // typed name is a known party. The party is a DubaiOTC party
            // whichever desk's rails are listed (railDesk above), so the
            // picker's default desk stands; its history is matched by full
            // account address, so an India bank this party was paid from still
            // ranks first when India's rails are shown.
            partySlug={partyExists ? slug : undefined}
            // NOT gated on the current asset. The rail decides the asset now
            // (see the RailPicker onChange), so gating the rail on the asset is
            // circular — and on 2026-08-27 it deadlocked: sitting on the INR
            // Angadiya rail, `compatibleKinds('INR')` hid every crypto rail, so
            // searching "trc" returned "No matching rails" and the asset
            // dropdown was itself pinned to INR. Neither door opened.
            // Consistency is kept by the asset FOLLOWING the pick, not by
            // hiding rails; the BFF refuses a mismatched pair regardless.
            loading={treasuryRows.isFetching || accounts === 'waiting'}
            readFailed={accounts === 'failed'}
            placeholder={direction === 'in' ? 'Receiving account' : 'Paying account'}
          />
          )}
        </Field>

        {/* AS OF — when the money moved (5 Sept 2026 door, see the `when`
            state; the chip row since 14 Sept 2026, sweep item 23: Now is an
            explicit choice, not a blank box, and the picker appears only when
            asked for). Rides the transfer route, the only posting this screen
            still makes. The chips render the echo and the back-date words
            themselves; `whenIso` / `whenWords` stay this screen's. */}
        <AsOfChips preset={urlWhen || undefined}
          value={when}
          // The reference carries the day — a new day, a new reference,
          // unless the old one may already be in the ledger (retireReference).
          onChange={(w) => { setWhen(w); retireReference(); }}
          disabled={submit.isPending}
        />
      </FieldRow>

      {/* MORE — the boxes used less than half the time (§7 rule 8): the
          desk's own cost inside an outgoing payment (0 of 32 payments) and
          Notes (12 of 72 entries). */}
      <MoreDetails label="More">
        {/* Part of an outgoing party payment can be the desk's own cost —
            a commission, a courier charge. Recording it here keeps it out of the
            party's balance and puts it in the P&L where it belongs. */}
        {direction === 'out' && (
          <FieldRow>
            <Field label="Fee on top, our cost" hint="optional">
              <div className="flex gap-2">
                <input className="input flex-1" value={costAmount} onChange={(e) => setCostAmount(e.target.value)} placeholder="0" inputMode="decimal" />
                <span className="self-center text-meta text-tg-hint">{displayAsset(asset)}</span>
              </div>
              {/* A cost that is not a number folds to zero and the split never
                  reaches the P&L — the payment posts in full and the commission
                  is gone. Say so instead. */}
              <ErrorLine>{costWords}</ErrorLine>
            </Field>
            {costN > 0 && (
              <Field label="Cost category">
                <select className="select w-full" value={costCategory} onChange={(e) => setCostCategory(e.target.value)}>
                  {EXPENSE_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </Field>
            )}
          </FieldRow>
        )}
        {direction === 'out' && costN > 0 && amountN > 0 && (
          <p className="text-meta text-tg-hint">
            {/* 21 Oct 2026 audit: "10,000 USDT·TRC to the party, 3 as other on top —
                10,003 leaves the account." restated the Amount box, the fee box and
                the category beside it. What the books need said is the sum that
                leaves the account. */}
            {`fee ${fmtAmount(costN, asset)} · ${fmtAmount(railDebitN, asset)}\u00a0${displayAsset(asset)} leaves the account`}
          </p>
        )}
        <Field label="Notes">
          <textarea className="input h-16" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything to remember" />
        </Field>
      </MoreDetails>

      {/* The dating line (see `dating`): a settle-later trade of this party's
          is still open in this asset and older than 12 h, while As of says
          Now. The tap sets As of to the trade's instant — the same door the
          chips open, so the reference and the echo follow (retireReference). */}
      {settlementsWanted && (settlementsRead === 'failed' ? (
        <StateLine tone="warn" action={retry(settlements.refetch)}>
          Open trades not read — try again.
        </StateLine>
      ) : settlementsRead === 'waiting' && (
        <StateLine>Reading open trades…</StateLine>
      ))}
      {dating && (
        <StateLine
          tone="warn"
          more="If this is its money, set when it moved."
          action={
            <button
              type="button"
              className="btn-link text-meta"
              disabled={submit.isPending}
              onClick={() => { setWhen(isoToDubaiWall(dating.at)); retireReference(); }}
            >
              Set to {fmtDay(dating.at)}
            </button>
          }
        >
          Settle-later trade from {fmtDay(dating.at)} still open in {displayAsset(dating.asset)}.
        </StateLine>
      )}

      {/* lg+: hairline separator + right-aligned, content-sized submit.
          21 Oct 2026 audit (the Book trade rule, applied here too): while a repeat
          panel is open the button drops to the secondary style, so the panel's own
          remedy ("Book it settle-later instead") is the one gold button. A restyle
          only — nothing is disabled. */}
      <div className="lg:flex lg:justify-end lg:border-t lg:border-stroke-soft lg:pt-5">
        <button
          className={`${repeat ? 'btn-secondary' : 'btn-primary'} w-full lg:w-auto lg:px-6`}
          disabled={submit.isPending || !amountOk(amount) || !slug
            // The party read and matched — its type decides the account (post() refuses otherwise).
            || !partyExists || lookup !== 'known'
            // A cost box that is not a plain number would be dropped in silence.
            // Only while that box is on screen — a dead button whose reason is
            // hidden on another direction is no better than a silent drop.
            || (direction === 'out' && costWords !== null)
            // A party movement without its rail would post half an entry.
            || !counterRail.slug
            // A back-date the BFF would refuse is refused here first.
            || !!whenWords}
          onClick={() => submit.mutate()}
        >
          {/* The date sits on the button, so the operator sees what they are
              committing to at the moment they commit — not only in the echo
              line further up the form (review, 5 Sept 2026). */}
          {submit.isPending ? 'Booking…'
            : `${direction === 'in' ? 'Record money in' : 'Record money out'}${whenIso && !whenWords ? ` as of ${fmtDay(whenIso)}` : ''}`}
        </button>
      </div>

      {/* A back-dated posting says its date back, so "✓" alone can never hide
          money that landed on the wrong day — read from what was POSTED
          (`posted`), never from the live field. A deduped answer says so
          instead: the ledger already held this reference and kept the first
          date. The line names amount, both sides and the reference (§6). */}
      {submit.isSuccess && posted && <PostedLine posted={posted} />}
      {/* Coins on a merchant's box with no trade behind them (see `nudge`).
          Sign law: positive = they owe the desk, so a payment OUT leaves them
          holding the desk's coins; a receipt IN leaves the desk holding
          theirs. The link opens Book trade with this movement's facts and
          settle-later ticked, so the trade is settled by this very entry. */}
      {nudge && posted && (
        <StateLine
          tone="warn"
          action={
            <Link
              to={tradeHref({
                party: posted.slug, direction: posted.direction, asset: posted.asset, amount: posted.amount,
                when: posted.asOf ? isoToDubaiWall(posted.asOf) : '', rail: posted.rail, later: true,
              })}
              className="btn-link text-meta"
            >
              Book the trade →
            </Link>
          }
        >
          <span className="whitespace-nowrap">{boxWords(nudge.held, posted.asset).replace(/^./, (c) => c.toUpperCase())} {displayAsset(foldAsset(posted.asset))}</span> — no trade behind it.
        </StateLine>
      )}
      {submit.isError && !isSentinel(submit.error) && !isNewRailRefusal(submit.error) && (
        <ErrorLine className="mt-1 text-body">{errorWords(submit.error)}</ErrorLine>
      )}
      {/* AN ACCOUNT THAT HAS NEVER HELD THIS ASSET. The books refuse to open an
          account on their own (a wrong asset would mint a phantom twin the
          append-only ledger keeps for ever), so the operator opens it in
          words. Operator, 12 Sept 2026, with Loki's euro notes in hand and
          "allow_new_rail_asset" typed hopefully into the notes box: "how to?"
          Shortened 14 Sept 2026 (§5.5) — the panel opens above the button,
          full width, with the consequence on the button (§7 rule 11). */}
      {submit.isError && isNewRailRefusal(submit.error) && (
        <div className="rounded-lg bg-warn-bg p-3 text-body space-y-2">
          <StateLine tone="warn">
            That account has never held {displayAsset(asset)}.
          </StateLine>
          <p className="text-meta text-tg-hint">
            {newRailHolds(submit.error).length
              ? `Holds ${newRailHolds(submit.error).map(displayAsset).join(' / ')} today`
              : 'Never posted to'}
            {' — check asset and account.'}
          </p>
          {/* Full width on the phone: the label names the asset and the
              account and wraps inside a content-sized button at 375px. */}
          <button
            type="button"
            className="btn-secondary text-body w-full lg:w-auto"
            disabled={submit.isPending || !!whenWords}
            // Remembered with the other yeses (confirmedRef), so a later
            // "post it again anyway" does not meet this door a second time.
            onClick={() => { confirmedRef.current.add('new_rail'); submit.mutate(); }}
          >
            Yes — open {displayAsset(asset)} on {railLabel} and post
          </button>
        </div>
      )}
      {repeat && (
        <RepeatPanel
          twin={repeat}
          // A back-date the BFF would refuse locks this door as well.
          pending={submit.isPending || !!whenWords}
          onAlreadyBooked={() => { clearTwin(); nav(-1); }}
          // Confirm the guard that refused, and only that one: the twin guard
          // (confirm_repeat) or, since 14 Sept 2026, the settled-trade guard
          // (confirm_money_moved_again). The yes is remembered in confirmedRef
          // so the re-POST carries every answer given so far.
          onPostAnyway={() => { confirmedRef.current.add(repeat.kind); submit.mutate(); }}
          // "Book it settle-later instead" (sweep item 39): this screen has no
          // settle-later switch of its own, so the remedy is a link to Book
          // trade with this entry's party, leg, wallet and instant prefilled
          // and ?later=1 ticked — the trade the money already in the books
          // should settle. Read by the panel for the "money" kind only.
          settleLater={{ to: tradeHref({ party: slug, direction, asset, amount, when, rail: counterRail.slug, later: true }) }}
        />
      )}

      {/* THE LAST TEN OF THIS KIND, newest first (operator, 16 Oct 2026:
          "Newest first"; ascending until then, by his 17 Sept words):
          receipts under Money in, payments under Money out, narrowed to the
          party once one is known. Under the form, on its own read; it never
          holds the button. */}
      <RecentEntries
        kind="money"
        direction={direction}
        partySlug={partyExists ? slug : undefined}
        title={direction === 'in'
          ? (selectedParty ? `Last 10 receipts from ${partyName(selectedParty)}` : 'Last 10 receipts')
          : (selectedParty ? `Last 10 payments to ${partyName(selectedParty)}` : 'Last 10 payments')}
      />
      </>
      )}
    </div>
  );
}

/** The BFF's "this rail has never held this asset" refusal (400 new_rail_asset). */
function isNewRailRefusal(e: unknown): boolean {
  return e instanceof ApiError && e.code === 'new_rail_asset';
}
/** What that rail holds today, from the refusal's own body. */
function newRailHolds(e: unknown): string[] {
  const holds = (e as ApiError)?.body?.holds;
  return Array.isArray(holds) ? holds.map(String) : [];
}
